import { EventType, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { AUTH_CONFIG } from './authConfig';

let msalInstance;
let msalBootstrapPromise;
let msalRedirectError = null;

function getCallbackParams(value) {
  const params = new URLSearchParams(value.startsWith('#') || value.startsWith('?')
    ? value.slice(1)
    : value);

  return {
    hasCallback: params.has('code') || params.has('error'),
    params
  };
}

function getCurrentCallbackState() {
  if (typeof window === 'undefined') {
    return {
      hasCallback: false,
      hasHashCallback: false,
      hasSearchCallback: false
    };
  }

  const hash = getCallbackParams(window.location.hash || '');
  const search = getCallbackParams(window.location.search || '');

  return {
    hasCallback: hash.hasCallback || search.hasCallback,
    hasHashCallback: hash.hasCallback,
    hasSearchCallback: search.hasCallback
  };
}

function clearCallbackFromUrl() {
  if (typeof window === 'undefined') return;

  const callbackState = getCurrentCallbackState();
  if (!callbackState.hasCallback) return;

  const url = new URL(window.location.href);

  if (callbackState.hasHashCallback) {
    url.hash = '';
  }

  if (callbackState.hasSearchCallback) {
    ['code', 'state', 'error', 'error_description', 'client_info', 'session_state'].forEach((key) => {
      url.searchParams.delete(key);
    });
  }

  window.history.replaceState(
    window.history.state,
    document.title,
    `${url.pathname}${url.search}${url.hash}`
  );
}

function setRedirectError(error) {
  msalRedirectError = error;
  if (typeof window !== 'undefined') {
    window.__execEyeEntraRedirectError = error;
  }
}

export function getMsalRedirectError() {
  if (msalRedirectError) return msalRedirectError;
  if (typeof window === 'undefined') return null;
  return window.__execEyeEntraRedirectError || null;
}

export function getMsalInstance() {
  if (msalInstance) return msalInstance;

  msalInstance = new PublicClientApplication({
    auth: {
      clientId: AUTH_CONFIG.entra.clientId,
      authority: AUTH_CONFIG.entra.authority,
      redirectUri: `${window.location.origin}/`,
      postLogoutRedirectUri: `${window.location.origin}/`,
      navigateToLoginRequestUrl: false
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true,
      temporaryCacheLocation: 'sessionStorage'
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          if (level === LogLevel.Error) {
            console.error('[ExecEye Entra]', message);
            return;
          }
          if (level === LogLevel.Warning) {
            console.warn('[ExecEye Entra]', message);
          }
        },
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false
      }
    }
  });

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
      msalInstance.setActiveAccount(event.payload.account);
    }

    if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && event.payload?.account) {
      msalInstance.setActiveAccount(event.payload.account);
    }

    if (event.eventType === EventType.LOGIN_FAILURE || event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
      console.error('[ExecEye Entra] Authentication event failed', {
        eventType: event.eventType,
        errorCode: event.error?.errorCode || event.error?.error || '',
        message: event.error?.message || ''
      });
    }
  });

  return msalInstance;
}

export async function initializeEntraAuth() {
  if (!AUTH_CONFIG.isEntra || typeof window === 'undefined') return null;
  if (msalBootstrapPromise) return msalBootstrapPromise;

  msalBootstrapPromise = (async () => {
    const instance = getMsalInstance();
    const callbackState = getCurrentCallbackState();

    try {
      await instance.initialize();
      const response = await instance.handleRedirectPromise();
      const account = response?.account
        || instance.getActiveAccount()
        || instance.getAllAccounts()[0]
        || null;

      if (account) {
        instance.setActiveAccount(account);
      }

      if (callbackState.hasCallback && account) {
        clearCallbackFromUrl();
      }

      if (callbackState.hasCallback && !account) {
        const error = new Error('Microsoft returned to ExecEye, but the app could not create a local sign-in session. Please try again after clearing the browser session.');
        error.errorCode = 'exec_eye_entra_session_missing';
        setRedirectError(error);
        clearCallbackFromUrl();
      }

      return response;
    } catch (error) {
      console.error('[ExecEye Entra] Redirect bootstrap failed', error);
      setRedirectError(error);
      if (callbackState.hasCallback) {
        clearCallbackFromUrl();
      }
      return null;
    }
  })();

  return msalBootstrapPromise;
}
